import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { Loader } from '@krakentech/coral';

import { useAuth } from '@/components/Providers/Auth';
import { useGlobalMessage } from '@/components/Providers/GlobalMessage';
import { useViewerId } from '@/hooks/accounts/useViewerId';
import { useObtainKrakenToken } from '@/hooks/auth/useObtainKrakenToken';
import { INTERNAL_PATHS } from '@/utils/urls';

type GuestOneTimeAccessProps = {
  refreshToken: string;
  redirectPath: string;
};

const GuestOneTimeAccess = ({
  refreshToken,
  redirectPath,
}: GuestOneTimeAccessProps) => {
  const { data, mutate, isPending, isSuccess, isError } =
    useObtainKrakenToken();
  const { refetch: refetchViewerId } = useViewerId();

  const { isAuthenticated, login } = useAuth();
  const { setGlobalMessage } = useGlobalMessage();
  const { replace, isReady } = useRouter();

  useEffect(() => {
    if (!isAuthenticated && isReady && !isPending) {
      mutate({
        input: {
          refreshToken,
        },
      });
    }
  }, [isAuthenticated, isPending, isReady, mutate, refreshToken]);

  useEffect(() => {
    // If the obtainKrakenToken mutation is successful, log the user in and redirect them to the provided path
    if (isSuccess) {
      const {
        obtainKrakenToken: { token: accessToken, refreshToken },
      } = data;

      login({
        accessToken,
        refreshToken,
      });

      // Need to refetch viewerId to get the updated user details, currently the viewerId is not updated
      // TODO: Investigate why the viewerId hook is not re-called
      refetchViewerId();

      const newPath = {
        pathname: redirectPath,
        query: {},
      };
      void replace(newPath, undefined, { shallow: true });
    }
  }, [data, isSuccess, login, redirectPath, refetchViewerId, replace]);

  useEffect(() => {
    if (isError) {
      // If the obtainKrakenToken mutation errors, redirect to the expired email link page (as well as clear the global generic message alert)
      setGlobalMessage({ message: null });
      void replace(INTERNAL_PATHS.EXPIRED_EMAIL_LINK.path);
    }
  }, [isError, replace, setGlobalMessage]);

  return <Loader type="linear" variant="indeterminate" />;
};

export default GuestOneTimeAccess;
